import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import styles from "./dropdown.module.css";

import {
  arrowRightWhiteIcon,
  cancelWhiteIcon,
  arrowDownWhite,
} from "../../../images";

import { renderValidUrl } from "../../../utils/constants";
import {
  formatCourseDateWithOrdinal,
  getCourseCalendarDates,
} from "../../../utils/time";

import brandingCourseImage from "../../../images/branding-course.jpg";
import uiuxCourseImage from "../../../images/uiux-course.jpg";

const COURSE_TYPES = [
  {
    key: "bootcamp",
    label: "Live Bootcamps",
  },
  {
    key: "short-course",
    label: "Short Courses",
  },
  {
    key: "self-paced",
    label: "Self-Paced Courses",
  },
  {
    key: "free-course",
    label: "Free Courses",
  },
  {
    key: "project",
    label: "Join a Project",
  },
];

const routeForCourse = (course) => {
  const type = course?.course_type?.type;

  if (type === "bootcamp") {
    return `/courses/${course.Slug}/${type}`;
  }

  if (type === "short-course") {
    return `/short-course/${course.id}`;
  }

  if (type === "self-paced") {
    return `/self-paced/${course.id}`;
  }

  if (
    type === "free-course" ||
    type === "free course"
  ) {
    return `/course-details/${course.id}`;
  }

  if (type === "project") {
    return `/project-details/${course.id}`;
  }

  if (type === "fast track") {
    return "/fast-track";
  }

  return `/course-landing/${
    course?.course_type?.id ||
    course?.id
  }`;
};

const formatDate = (course, regionName) => {
  const { startDate } = getCourseCalendarDates(course, regionName);
  const value = startDate || course?.date || course?.cohortDate;

  return formatCourseDateWithOrdinal(value);
};

const CourseCard = ({
  course,
  onOpen,
  isEnrolled,
  index,
  regionName,
}) => {
  const category =
    course?.pill?.[0]?.pill1 ||
    course?.groupName ||
    course?.course_type?.name ||
    "Course";

  const normalizedCategory =
    category.toLowerCase();

  let fallbackImage;

  if (
    normalizedCategory.includes(
      "brand"
    )
  ) {
    fallbackImage =
      brandingCourseImage;
  } else if (
    normalizedCategory.includes("ui") ||
    normalizedCategory.includes("ux") ||
    normalizedCategory.includes(
      "design"
    )
  ) {
    fallbackImage = uiuxCourseImage;
  } else {
    fallbackImage =
      Number(course?.id || 0) %
        2 ===
      0
        ? brandingCourseImage
        : uiuxCourseImage;
  }

  const image =
    course?.thumbnail?.url
      ? renderValidUrl(
          course.thumbnail.url
        )
      : fallbackImage;

  const title =
    course?.name ||
    course?.title ||
    category;

  const date = formatDate(course, regionName);

  const handleKeyDown = (
    event
  ) => {
    if (
      event.key === "Enter" ||
      event.key === " "
    ) {
      event.preventDefault();

      onOpen(course);
    }
  };

  return (
    <article
      className={styles.card}
      role="button"
      tabIndex={0}
      data-card-index={index}
      onClick={() =>
        onOpen(course)
      }
      onKeyDown={handleKeyDown}
    >
      <div
        className={
          styles.card_image
        }
      >
        {image ? (
          <img
            src={image}
            alt={title}
          />
        ) : (
          <div
            className={
              styles.image_placeholder
            }
          />
        )}
      </div>

      <div
        className={
          styles.card_content
        }
      >
        <span
          className={styles.badge}
        >
          {category}
        </span>

        <div
          className={
            styles.card_title_row
          }
        >
          <h3>{title}</h3>

          {date && (
            <time>{date}</time>
          )}
        </div>

        <p>
          {course?.description ||
            "Build practical skills with guided learning and real-world projects."}
        </p>

        <button
          type="button"
          className={
            isEnrolled
              ? styles.enrolled_button
              : ""
          }
          onClick={(event) => {
            event.stopPropagation();

            onOpen(course);
          }}
        >
          {isEnrolled
            ? "Enrolled"
            : "Enroll Now"}
        </button>
      </div>
    </article>
  );
};

const Dropdown = ({
  show,
  menu,
  handleMenu,
  setDropdown,
  topOffset = 118,
}) => {
  const navigate = useNavigate();

  const user = useSelector(
    (state) => state.user
  );

  const defaultRegion = useSelector(
    (state) => state.formData?.defaultRegion
  );

  const dropdownRef =
    useRef(null);

  const closeTimer =
    useRef(null);

  const [width, setWidth] =
    useState(
      window.innerWidth
    );

  const [
    activeType,
    setActiveType,
  ] = useState("bootcamp");

  const [
    mobileView,
    setMobileView,
  ] = useState("types");

  const isMobile =
    width < 992;

  useEffect(() => {
    const onResize = () => {
      setWidth(
        window.innerWidth
      );
    };

    window.addEventListener(
      "resize",
      onResize
    );

    return () => {
      window.removeEventListener(
        "resize",
        onResize
      );
    };
  }, []);

  useEffect(() => {
    if (show && isMobile) {
      setMobileView("types");
    }
  }, [show, isMobile]);

  useEffect(() => {
    return () => {
      if (
        closeTimer.current
      ) {
        clearTimeout(
          closeTimer.current
        );
      }
    };
  }, []);

  const courses = useMemo(
    () =>
      menu?.data?.data || [],
    [menu]
  );

  const coursesByType =
    useMemo(() => {
      return COURSE_TYPES.reduce(
        (acc, type) => {
          acc[type.key] =
            courses.filter(
              (course) => {
                const courseType =
                  course
                    ?.course_type
                    ?.type;

                if (
                  type.key ===
                  "free-course"
                ) {
                  return (
                    courseType ===
                      "free-course" ||
                    courseType ===
                      "free course"
                  );
                }

                return (
                  courseType ===
                  type.key
                );
              }
            );

          return acc;
        },
        {}
      );
    }, [courses]);

  const activeCourseType =
    COURSE_TYPES.find(
      (type) =>
        type.key ===
        activeType
    );

  const activeCourses =
    coursesByType[
      activeType
    ] || [];

  const enrolledCourses =
    user?.enrolledCourses ||
    user?.enrolled_courses ||
    [];

  const isEnrolled = (
    course
  ) => {
    return enrolledCourses.some(
      (item) => {
        const itemId =
          typeof item ===
          "object"
            ? item?.id ||
              item?.course_id
            : item;

        return (
          Number(itemId) ===
          Number(course?.id)
        );
      }
    );
  };

  const openCourse = (
    course
  ) => {
    setDropdown(false);

    if (isMobile) {
      handleMenu();
    }

    navigate(
      routeForCourse(course),
      {
        state: {
          courseId:
            course
              ?.course_type
              ?.id ||
            course?.id,
        },
      }
    );
  };

  const keepOpen = () => {
    if (
      closeTimer.current
    ) {
      clearTimeout(
        closeTimer.current
      );
    }
  };

  const closeOnHoverOut =
    () => {
      closeTimer.current =
        setTimeout(() => {
          setDropdown(false);
        }, 140);
    };

  useEffect(() => {
    if (isMobile) {
      return undefined;
    }

    const onDocumentMouseDown =
      (event) => {
        if (
          dropdownRef.current?.contains(
            event.target
          ) ||
          event.target.closest(
            ".button_parent"
          )
        ) {
          return;
        }

        setDropdown(false);
      };

    document.addEventListener(
      "mousedown",
      onDocumentMouseDown
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        onDocumentMouseDown
      );
    };
  }, [
    setDropdown,
    isMobile,
  ]);

  const selectCourseType = (
    type
  ) => {
    setActiveType(type.key);

    if (isMobile) {
      setMobileView(
        "courses"
      );
    }
  };

  const handleDropdownBack =
    (event) => {
      event.stopPropagation();

      /*
       * If viewing course cards,
       * return to category list.
       */
      if (
        isMobile &&
        mobileView ===
          "courses"
      ) {
        setMobileView(
          "types"
        );

        return;
      }

      /*
       * If viewing category list,
       * return to normal mobile nav.
       */
      setDropdown(false);
    };

  const handleDropdownClose =
    (event) => {
      event.stopPropagation();

      setMobileView(
        "types"
      );

      setDropdown(false);

      /*
       * Completely close
       * mobile navigation.
       */
      if (isMobile) {
        handleMenu();
      }
    };

  const handleViewAll =
    () => {
      setDropdown(false);

      if (isMobile) {
        handleMenu();
      }

      navigate(
        "/dashboard/recommended-courses?section=bootcamps"
      );
    };

  if (!show) {
    return null;
  }

  return (
    <div
      ref={dropdownRef}
      className={
        styles.dropdown_body
      }
      style={{
        "--dropdown-top": `${topOffset}px`,
      }}
    >
      <div
        className={
          styles.positioning
        }
        onMouseEnter={
          !isMobile
            ? keepOpen
            : undefined
        }
        onMouseLeave={
          !isMobile
            ? closeOnHoverOut
            : undefined
        }
        onMouseOver={() => {
          if (!isMobile) {
            setDropdown(true);
          }
        }}
        onClick={(event) =>
          event.stopPropagation()
        }
      >
        {/* MOBILE HEADER */}

        <div
          className={
            styles.hidden
          }
        >
          <button
            type="button"
            aria-label="Back"
            className={
              styles.back_button
            }
            onClick={
              handleDropdownBack
            }
          >
            <img
              src={
                arrowRightWhiteIcon
              }
              alt=""
            />
          </button>

          <h1>
            {mobileView ===
            "courses"
              ? activeCourseType?.label
              : "For Student"}
          </h1>

          <button
            type="button"
            aria-label="Close"
            className={
              styles.close_button
            }
            onClick={
              handleDropdownClose
            }
          >
            <img
              src={
                cancelWhiteIcon
              }
              alt=""
              className={
                styles.arrow_icon
              }
            />
          </button>
        </div>

        <div
          className={
            styles.student_menu
          }
        >
          {(!isMobile ||
            mobileView ===
              "types") && (
            <nav
              className={
                styles.type_list
              }
              aria-label="Course types"
            >
              {COURSE_TYPES.map(
                (type) => (
                  <button
                    key={
                      type.key
                    }
                    type="button"
                    className={`${
                      styles.type_item
                    } ${
                      activeType ===
                      type.key
                        ? styles.type_item_active
                        : ""
                    }`}
                    onMouseEnter={() => {
                      if (
                        !isMobile
                      ) {
                        setActiveType(
                          type.key
                        );
                      }
                    }}
                    onClick={() =>
                      selectCourseType(
                        type
                      )
                    }
                  >
                    <span>
                      {
                        type.label
                      }
                    </span>

                    <img
                      src={
                        arrowDownWhite
                      }
                      alt=""
                    />
                  </button>
                )
              )}
            </nav>
          )}

          {(!isMobile ||
            mobileView ===
              "courses") && (
            <div
              className={
                styles.course_panel
              }
            >
              <div
                className={
                  styles.cards
                }
              >
                {activeCourses
                  .slice(0, 2)
                  .map(
                    (
                      course,
                      index
                    ) => (
                      <CourseCard
                        key={
                          course.id
                        }
                        course={
                          course
                        }
                        index={
                          index
                        }
                        isEnrolled={isEnrolled(
                          course
                        )}
                        regionName={
                          defaultRegion
                        }
                        onOpen={
                          openCourse
                        }
                      />
                    )
                  )}

                {!activeCourses.length && (
                  <p
                    className={
                      styles.empty
                    }
                  >
                    No courses
                    available
                    yet.
                  </p>
                )}
              </div>

              {activeCourses.length >
                0 &&
                (isMobile ||
                  activeCourses.length >
                    2) && (
                  <button
                    type="button"
                    className={
                      styles.view_all
                    }
                    onClick={
                      handleViewAll
                    }
                  >
                    View All
                  </button>
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dropdown;
